import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * V4.9.16 — Custom, fully in-app date picker.
 *
 * Replaces the native `<input type="date">` for the Reports/ticket "Custom
 * range" filter. The native control's calendar popup is browser-specific and,
 * when the field is narrow, its tiny calendar glyph is an unreliable click
 * target (the "To" field often wouldn't open). This component renders its OWN
 * calendar (pure DOM) so it behaves IDENTICALLY on every browser/OS, opens on a
 * click ANYWHERE in the field, and is styled to the app theme. It mirrors the
 * MultiSelect popover pattern exactly (ref + outside-click close + the
 * `.is-open` z-index lift) so it floats above the results table.
 *
 * Value contract: `value` and `onChange` use a plain `yyyy-mm-dd` string (or ''
 * for cleared) built from LOCAL calendar parts — no timezone drift.
 */
interface Props {
  /** Field label (e.g. "From" / "To"). */
  label?: string;
  /** Current value as 'yyyy-mm-dd', or '' when unset. */
  value: string;
  /** Called with 'yyyy-mm-dd' on pick, or '' on clear. */
  onChange: (next: string) => void;
  ariaLabel?: string;
  placeholder?: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n: number) => String(n).padStart(2, '0');
const toYmd = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

function parseYmd(s: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '');
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
}

interface Cell {
  y: number;
  m: number;
  d: number;
  inMonth: boolean;
}

export function DateField({ label, value, onChange, ariaLabel, placeholder = 'Pick a date' }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const parsed = parseYmd(value);
  const now = new Date();
  const [view, setView] = useState(() =>
    parsed ? new Date(parsed.y, parsed.m, 1) : new Date(now.getFullYear(), now.getMonth(), 1)
  );

  // Sync the visible month to the selected date (or today) each time we open.
  useEffect(() => {
    if (!open) return;
    const p = parseYmd(value);
    const t = new Date();
    setView(p ? new Date(p.y, p.m, 1) : new Date(t.getFullYear(), t.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside-click or Escape — same as MultiSelect.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const display = parsed ? `${pad(parsed.d)} ${MONTHS_SHORT[parsed.m]} ${parsed.y}` : '';

  const cells = useMemo<Cell[]>(() => {
    const y = view.getFullYear();
    const m = view.getMonth();
    const firstDow = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const out: Cell[] = [];
    for (let i = 0; i < firstDow; i++) {
      const dd = new Date(y, m, 1 - (firstDow - i));
      out.push({ y: dd.getFullYear(), m: dd.getMonth(), d: dd.getDate(), inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) out.push({ y, m, d, inMonth: true });
    while (out.length < 42) {
      const last = out[out.length - 1];
      const dd = new Date(last.y, last.m, last.d + 1);
      out.push({ y: dd.getFullYear(), m: dd.getMonth(), d: dd.getDate(), inMonth: dd.getMonth() === m });
    }
    return out;
  }, [view]);

  const pick = (c: { y: number; m: number; d: number }) => {
    onChange(toYmd(c.y, c.m, c.d));
    setOpen(false);
  };

  const isSel = (c: Cell) => !!parsed && c.y === parsed.y && c.m === parsed.m && c.d === parsed.d;
  const isToday = (c: Cell) =>
    c.y === now.getFullYear() && c.m === now.getMonth() && c.d === now.getDate();

  return (
    <div ref={ref} className={`form-group date-field ${open ? 'is-open' : ''}`} style={{ margin: 0 }}>
      {label ? <label className="form-label">{label}</label> : null}
      <button
        type="button"
        className="form-select date-field-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={ariaLabel ?? label}
      >
        <span className={display ? undefined : 'date-field-placeholder'}>{display || placeholder}</span>
        <span className="date-field-icon" aria-hidden>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M3 10h18M8 2v4M16 2v4" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="date-field-menu" role="dialog" aria-label="Choose date">
          <div className="date-field-head">
            <button
              type="button"
              className="date-field-nav"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="date-field-month">
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </span>
            <button
              type="button"
              className="date-field-nav"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="date-field-grid date-field-dow">
            {WEEKDAYS.map((w) => (
              <span key={w} className="date-field-dow-cell">
                {w}
              </span>
            ))}
          </div>
          <div className="date-field-grid">
            {cells.map((c, i) => (
              <button
                key={i}
                type="button"
                className={
                  'date-field-day' +
                  (c.inMonth ? '' : ' is-out') +
                  (isToday(c) ? ' is-today' : '') +
                  (isSel(c) ? ' is-selected' : '')
                }
                onClick={() => pick(c)}
              >
                {c.d}
              </button>
            ))}
          </div>
          <div className="date-field-foot">
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                const t = new Date();
                pick({ y: t.getFullYear(), m: t.getMonth(), d: t.getDate() });
              }}
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
